import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import { PhotoManagerContext } from "../index";
import { PhotoGridContext } from "../PhotoGrid";
import {
  useEventData,
  useModalStates,
  getUrlParams
} from "../BreadcrumbsUtilities";
import ShareSocialPopup from "../../../ShareSocialPopup";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import { useEditAlbumName } from "../useEditAlbumName";
import { PhotoGridBreadcrumbProps } from "./types";
import LoadingSpinner from "./LoadingSpinner";
import AlbumNameEditor from "./AlbumNameEditor";
import AlbumActions from "./AlbumActions";
import { hierarchyEntitiesType } from "../types";
import { usePopupClickOutside } from "../../../../hooks/usePopupClickOutside";
import AlbumTag from "../../../AlbumTag/components/index";
const shortcutIcon = require("../../styles/images/shortcut-icon.svg");
const GreenArrow = require("../../styles/images/GreenArrow.svg");

const PhotoGridBreadcrumb: React.FC<PhotoGridBreadcrumbProps> = ({}) => {
  const [showHiddenLinks, setShowHiddenLinks] = useState<boolean>(false);
  const hiddenLinksPopupRef = useRef<HTMLDivElement>(null);
  usePopupClickOutside(hiddenLinksPopupRef, () => setShowHiddenLinks(false));
  const context = useContext(PhotoManagerContext);
  const photogridContext = useContext(PhotoGridContext);
  const loadingAlbum =
    photogridContext?.loadingAlbum || context?.albumLoadingEventId;
  const selectedAlbum =
    context?.selectedAlbum || photogridContext?.selectedAlbumDetails;
  const isDeeplyNested =
    selectedAlbum &&
    selectedAlbum?.hierarchy &&
    selectedAlbum.hierarchy.length > 2;
  const hiddenLinks =
    selectedAlbum &&
    selectedAlbum?.hierarchy &&
    selectedAlbum.hierarchy.slice(0, -2);
  const shownLinks =
    selectedAlbum &&
    selectedAlbum?.hierarchy &&
    selectedAlbum.hierarchy.slice(-2);

  const [albumName, setAlbumName] = useState("");
  const [showUploaderOption, setShowUploaderOption] = useState(false);
  const [showAlbumTagsModal, setShowAlbumTagsModal] = useState(false);
  const {
    activeShareModalEventId,
    setActiveShareModalEventId,
    showDeleteModal,
    setShowDeleteModal
  } = useModalStates();

  // Simple function to handle album name updates
  const handleAlbumNameUpdate = (album: Album) => {
    context?.onEditSuccessUpdateAlbum?.(album);
    setAlbumName(album.name);
  };

  const {
    albumsInEditMode,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange
  } = useEditAlbumName(
    context?.updateAlbumsList ?? (() => {}),
    albums => albums.length > 0 && setAlbumName(albums[0].name),
    null,
    handleAlbumNameUpdate
  );

  const handleBreadcrumbClick = () => {
    context?.navigateToRoot?.();
    window.history.pushState({}, "", "/library");
    context?.setSelectedView(null);
  };

  useEffect(() => {
    if (selectedAlbum) {
      setAlbumName(selectedAlbum.name || "");
    }
  }, [selectedAlbum]);


  const breadcrumbHeight = useMemo(() => {
    if (!context) return "";

    const { isMobile } = context;
    const isLoading = loadingAlbum;

    if (isMobile && isLoading) {
      return "60px";
    }

    return "";
  }, [loadingAlbum, context]);

  const breadcrumbClasses = [
    "ws-breadcrumbs",
    "ws-action-and-breadcrumbs",
    "ws-actn-mb-bredcrmb",
    showUploaderOption ? "upload-optn-opened" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const renderParentLinks = () => {
    if (isDeeplyNested) {
      return (
        <>
          <div className="deeply-nested-view" ref={hiddenLinksPopupRef}>
            <button
              type="button"
              className="green-text geo-text-btn"
              onClick={() => setShowHiddenLinks(!showHiddenLinks)}
            >
              ...
            </button>
            {showHiddenLinks && (
              <div className="hidden-links-card">
                {hiddenLinks?.map((link: hierarchyEntitiesType) => {
                  return (
                    <div>
                      <button
                        type="button"
                        className="hidden-links geo-text-btn text-limit black-text"
                        onClick={e =>
                          context?.handleBreadcrumbClick(
                            link.id,
                            link.entityType
                          )
                        }
                      >
                        {link.entityType === "LibraryFolderReference" && (
                          <img src={shortcutIcon} />
                        )}
                        {link.name}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <span className="grey-text bc-links-divider"> / </span>
          {shownLinks?.slice(0, 1).map((parent: hierarchyEntitiesType) => {
            return (
              <>
                <button
                  type="button"
                  className="green-text geo-text-btn text-limit-w"
                  onClick={e => {
                    context?.handleBreadcrumbClick(
                      parent.id,
                      parent.entityType,
                      parent.entityType === "LibraryFolderReference"
                        ? "event"
                        : undefined
                    );
                  }}
                >
                  {parent.entityType === "LibraryFolderReference" && (
                    <img src={shortcutIcon} />
                  )}
                  {parent.name}
                </button>
                <span className="grey-text bc-links-divider"> / </span>
              </>
            );
          })}
        </>
      );
    }
    return (
      <>
        {selectedAlbum &&
          selectedAlbum?.hierarchy
            .slice(0, 1)
            .map((parent: hierarchyEntitiesType) => {
              return (
                <>
                  <button
                    type="button"
                    className="green-text geo-text-btn text-limit-w"
                    aria-label="Back to Library"
                    onClick={e =>
                      context?.handleBreadcrumbClick(
                        parent.id,
                        parent.entityType,
                        parent.entityType === "LibraryFolderReference"
                          ? "event"
                          : undefined
                      )
                    }
                  >
                    {parent.entityType === "LibraryFolderReference" && (
                      <img src={shortcutIcon} />
                    )}

                    {parent.name}
                  </button>
                  <span className="grey-text bc-links-divider"> / </span>
                </>
              );
            })}
      </>
    );
  };

  return (
    <>
      <div
        className={breadcrumbClasses}
        style={{
          height: breadcrumbHeight
        }}
      >
        <div className="photgrid-breadcrumb-left-content">
          <p className="ws-head-txt">
            <button
              type="button"
              className="green-text geo-text-btn"
              aria-label="Back to Library"
              onClick={handleBreadcrumbClick}
            >
              <img
                src={GreenArrow}
                alt="Back to library"
                style={{ marginRight: 5 }}
              />
              Library
            </button>
            <span className="grey-text bc-links-divider">/</span>{" "}
            {!loadingAlbum && renderParentLinks()}
            {loadingAlbum ? (
              <LoadingSpinner />
            ) : (
              <span
                className="grey-text grey-text-limit-w"
                data-toggle="tooltip"
                data-placement="bottom"
                data-original-title={selectedAlbum?.name ?? ""}
              >
                {selectedAlbum?.name ?? ""}
              </span>
            )}
          </p>

          <div className="black-text">
            {loadingAlbum ? (
              <LoadingSpinner />
            ) : (
              <>
                {selectedAlbum && context?.selectedEvent && (
                  <AlbumNameEditor
                    albumName={albumName}
                    selectedAlbum={selectedAlbum}
                    selectedEvent={context.selectedEvent}
                    albumsInEditMode={albumsInEditMode}
                    updateDraftName={updateDraftName}
                    handleKeyDownAction={handleKeyDownAction}
                    submitNameChange={submitNameChange}
                    toggleEditMode={toggleEditMode}
                    showActionTooltip={context?.showActionTooltip}
                  />
                )}
                <span className="ws-head-photo-count">
                  <span className="ws-head-photo-dot">
                    &nbsp;&nbsp;•&nbsp;&nbsp;
                  </span>
                  {selectedAlbum?.photosCount} Photos
                </span>
              </>
            )}
          </div>
        </div>
        {loadingAlbum && !context?.isMobile ? (
          <LoadingSpinner />
        ) : (
          context &&
          context.selectedEvent && (
            <AlbumActions
              selectedEvent={context.selectedEvent}
              selectedAlbum={selectedAlbum}
              context={context}
              setShowUploaderOption={setShowUploaderOption}
              showUploaderOption={showUploaderOption}
              photogridContext={photogridContext}
              setShowDeleteModal={setShowDeleteModal}
              setActiveShareModalEventId={setActiveShareModalEventId}
              setShowAlbumTagsModal={setShowAlbumTagsModal}
            />
          )
        )}
      </div>

      {showDeleteModal && context && selectedAlbum && (
        <ConfirmDeleteModal
          albumId={selectedAlbum.id}
          onClose={() => setShowDeleteModal(false)}
          onDeleteSuccess={() => {
            context.onDeleteSuccess(selectedAlbum.id);
            setShowDeleteModal(false);
          }}
          albumName={selectedAlbum.name}
        />
      )}

      {activeShareModalEventId && (
        <div className="event-share-modal">
          <ShareSocialPopup
            galleryURL={selectedAlbum?.shareAlbumURL ?? ""}
            hideShareGalleryModal={() => setActiveShareModalEventId(null)}
            text="Share with participants or just show off ;)"
          />
        </div>
      )}

      {showAlbumTagsModal && selectedAlbum && selectedAlbum.canManageTags && (
        <div 
          className="tag-popup-container" 
          style={{ display: 'block' }}
          onClick={(e) => {
            // Close modal if clicking on backdrop
            if (e.target === e.currentTarget) {
              setShowAlbumTagsModal(false);
            }
          }}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <AlbumTag
              organizationId={selectedAlbum.organizationId}
              albumName={selectedAlbum.name}
              photosCount={selectedAlbum.photosCount}
              appliedTags={selectedAlbum.appliedTags}
              contentTagsAPI={selectedAlbum.contentTagsApi}
              albumId={selectedAlbum.id}
              onClose={() => setShowAlbumTagsModal(false)}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoGridBreadcrumb;
